import { useCallback, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useCreateNote } from './useCreateNote'
import { useSaveNote } from './useSaveNote'
import { useNotification } from '../providers/NotificationProvider'
import { NoteType, SourceDatabase, type Note, filenameToKey } from '../utils/note'
import { AuthType } from '../utils/User'
import { errorIcon } from '../components/Notification'
import { useAuthenticatedUser } from '../providers/UserProvider'
import { useCreateImportLogNote } from '../utils/import/createImportLogNote'
import { useImportConflictDialog } from '../components/ImportConflictDialog'
import {
  createEmptyImportStats,
  createCompletionNotification,
  type ImportSourceType,
} from '../utils/import/core'

import { createProgressManager } from '../utils/import/progressManager'
import { buildImportBatch } from '../utils/import/batchBuilder'
import {
  createAttachmentStrategy,
  type StrategyHooks,
} from '../utils/import/attachmentStrategies'
import { MAX_ATTACHMENT_SIZE_BYTES } from '../utils/import/config'
import { runImport, type EngineOptions } from '../utils/import/engine'
import { createConflictResolver } from '../utils/import/conflictResolver'
import { cachedNotesContext, getCachedData } from '../contexts/CachedNotes'
import { cacheKeyFromNoteType } from '../utils/queryKeyFactory'
import { createImportedNotesFolder } from '../utils/import/folderHelpers'

/**
 * Hook for importing entire folder hierarchies into NotePlan
 *
 * Features:
 * - Folder structure preservation using webkitdirectory API
 * - Hidden file/folder filtering (skips .DS_Store, .git, etc.)
 * - Progress notifications with percentage updates
 * - Comprehensive error handling and logging
 * - Chains mutations via callbacks following existing patterns
 * - Creates folders first, then imports notes
 */

// Types are now imported from shared module above

/**
 * Adds all parent folders to the folders set
 */
/**
 * Maps AuthType to SourceDatabase for strategy selection
 * For hybrid users (CLOUDKIT_SUPABASE), defaults to CloudKit for private notes
 */
function getSourceDatabase(authType: AuthType): SourceDatabase {
  switch (authType) {
    case AuthType.CLOUDKIT:
    case AuthType.CLOUDKIT_SUPABASE: {
      return SourceDatabase.CLOUDKIT
    }
    case AuthType.SUPABASE: {
      return SourceDatabase.SUPABASE
    }
    default: {
      throw new Error(`Unsupported auth type: ${String(authType)}`)
    }
  }
}

export function useImportFolder() {
  const cachedNotesQueryClient = useQueryClient({ context: cachedNotesContext })
  const user = useAuthenticatedUser()
  const createNote = useCreateNote()
  const saveNote = useSaveNote()
  const { showNotification, hideNotification, replaceNotification } =
    useNotification()
  const createImportLogNote = useCreateImportLogNote()
  const { showDialog, DialogComponent } = useImportConflictDialog()

  // Prevent double imports
  const importInProgressReference = useRef(false)

  const handleImportFolder = useCallback(
    async (files: FileList, sourceType?: ImportSourceType) => {
      // Guard against double imports
      if (importInProgressReference.current) {
        return
      }

      importInProgressReference.current = true

      // Capture file list for import log (local variable to avoid stale state)
      const attemptedFiles = [...files].map((file) => file.webkitRelativePath || file.name)

      try {
        const batchResult = await buildImportBatch(files, sourceType)
        const { items, warnings, hints } = batchResult
        const stats = createEmptyImportStats()

        // 2. Calculate correct total and setup progress manager
        const totalOperations = items.length
        const progressManager = createProgressManager(
          showNotification,
          replaceNotification
        )

        // Handle edge case: nothing to import
        if (totalOperations === 0) {
          progressManager.done(createCompletionNotification(stats, 0))
          return
        }

        progressManager.begin(totalOperations)

        // 3. Create AttachmentStrategy for backend-specific attachment handling
        const sourceDatabase = getSourceDatabase(user.authType)
        const strategyHooks: StrategyHooks = {
          useCreateNote: () => createNote,
          useSaveNote: () => saveNote,
        }
        const attachmentStrategy = createAttachmentStrategy(
          sourceDatabase,
          strategyHooks
        )

        // 4. Create conflict resolver
        const conflictResolver = createConflictResolver(showDialog)

        // Create wrapper functions for the mutations
        const createNoteFunction = async (parameters: {
          recordName: string
          noteType: NoteType
          filename: string
          content: string
          parent: string | null
          isDir?: boolean
        }): Promise<Note> => {
          return createNote.mutateAsync(parameters)
        }

        const saveNoteFunction = async (parameters: {
          content: string
          attachments?: string[]
          recordName: string
          filename: string
          parent: string | null
          noteType: NoteType
          modificationDate: Date
        }): Promise<Note> => {
          return saveNote.mutateAsync(parameters)
        }

        // Live cache getters - read from TanStack Query cache on-demand
        const getProjectNotesMap = (): Map<string, Note> => {
          const projectKey = cacheKeyFromNoteType(NoteType.PROJECT_NOTE, user)
          return getCachedData(cachedNotesQueryClient, projectKey)
        }

        const getCalendarNotesByDate = (): Map<string, Note[]> => {
          const calendarKey = cacheKeyFromNoteType(NoteType.CALENDAR_NOTE, user)
          const calendarMap = getCachedData(cachedNotesQueryClient, calendarKey)
          const grouped = new Map<string, Note[]>()
          for (const note of calendarMap.values()) {
            const key = filenameToKey(note.filename)
            if (!key) continue
            const array = grouped.get(key) ?? []
            array.push(note)
            grouped.set(key, array)
          }
          return grouped
        }

        // Create "Imported Notes" folder with auto-incrementing suffix
        const { folderName, folder } = await createImportedNotesFolder(
          createNoteFunction,
          getProjectNotesMap()
        )

        // Map the folder for the engine
        const folderIdMap = new Map<string, string>()
        folderIdMap.set(folderName, folder.recordName ?? '')

        // Prepare engine options
        const engineOptions: EngineOptions = {
          attachmentStrategy,
          onProgress: (_done, _total) => {
            progressManager.step(1)
          },
          onConflict: conflictResolver.resolve,
          maxAttachmentBytes: MAX_ATTACHMENT_SIZE_BYTES, // 50MB default
          authType: user.authType,
          getProjectNotesMap,
          getCalendarNotesByDate,
          folderIdMap,
          targetRootFolder: folderName,
          createNote: createNoteFunction,
          saveNote: saveNoteFunction,
        }

        // 5. Run the unified import engine
        const result = await runImport(items, engineOptions)

        // 6. Aggregate results and notify
        progressManager.done(
          createCompletionNotification(
            result.stats,
            result.attachmentsFailed.length
          )
        )

        // Create import log with enhanced logging system
        createImportLogNote({
          filesAttempted: attemptedFiles,
          stats: result.stats,
          errors: result.errors,
          warnings: [...warnings, ...result.warnings],
          hints: [...hints, ...result.hints],
          attachmentsFailed: result.attachmentsFailed,
        })
      } catch {
        hideNotification()
        showNotification({
          title: 'Import Error',
          message: 'Failed to analyze folder contents. Please try again.',
          icon: errorIcon,
        })
      } finally {
        importInProgressReference.current = false
      }
    },
    [
      cachedNotesQueryClient,
      showNotification,
      replaceNotification,
      user,
      createImportLogNote,
      createNote,
      saveNote,
      showDialog,
      hideNotification,
    ]
  )

  return { handleImportFolder, DialogComponent }
}
